// The venue's stations as the Boot screens name them: a boot PC is known by its MAC, and linked to a station — "PC-03"
// reads better. Read from the live floor snapshot (`floor:read`), which also says which PCs are idle, so "Edit on PC" can
// offer only PCs nobody is playing on. If the floor cannot be read the PCs are listed by MAC and the api refuses a busy one.

import { useTranslation } from '@clavian/i18n/react';
import type { FloorStationState } from '@clavian/api-client';
import { useMemo } from 'react';

import { useFloor } from '../floor/queries';

export interface BootStation {
  readonly id: string;
  readonly label: string;
  /** Online and locked: nobody is playing, so an edit boot or a restart is safe. `undefined` when the floor is not known. */
  readonly idle: boolean | undefined;
}

/** A station's name, or "PC 3" for one that goes by its number alone. */
export function stationLabel(station: Pick<FloorStationState, 'name' | 'number'>, numbered: (number: number) => string): string {
  return station.name ?? numbered(station.number);
}

export function useBootStations(organizationId: string, venueId: string): ReadonlyMap<string, BootStation> {
  const { t } = useTranslation('console');
  const floor = useFloor(organizationId, venueId);
  return useMemo(() => {
    const map = new Map<string, BootStation>();
    for (const station of floor.data?.stations ?? []) {
      map.set(station.stationId, {
        id: station.stationId,
        label: stationLabel(station, (number) => t('boot.pcs.stationNumber', { number })),
        idle: station.online && station.mode === 'locked',
      });
    }
    return map;
  }, [floor.data, t]);
}
